// @flow
import {
  Grid,
  Typography,
  makeStyles,
  IconButton,
  TextField,
} from '@material-ui/core';
import React, {useState, useEffect} from 'react';
import {Edit, Check, Close} from '@material-ui/icons';
import {colors} from '../../../../../assets/styles/colors';
import FunnelColumnList from './FunnelColumnList/FunnelColumnList';
import {useCurrentMultichatFunnel} from '../../utils';
import {useActions} from '../../../../../utils/globalStore';
import {actions} from '../../duck';
import {editMultichatFunnelService} from '../../promises';
import {useAlert} from '../../../../../elements/Alert/AlertContext';

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
  },
  header: {
    background: 'white',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '10px',
    padding: '8px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '64px',
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  name: {
    fontWeight: 600,
    fontSize: 16,
    color: colors.grey900,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
}));

const FunnelItem = ({
  draggingType,
  search,
}: {
  draggingType: string | null,
  search?: string,
}) => {
  const classes = useStyles();
  const currentFunnel = useCurrentMultichatFunnel();
  const {showAlert} = useAlert();

  const setCurrentMultichatFunnel = useActions(
    actions.setCurrentMultichatFunnel
  );

  const [isEditing, setIsEditing] = useState(false);
  const [funnelName, setFunnelName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentFunnel && !isEditing) {
      setFunnelName(currentFunnel.name);
    }
  }, [currentFunnel, isEditing]);

  if (!currentFunnel) {
    return null;
  }

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setFunnelName(currentFunnel.name);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!funnelName.trim() || funnelName === currentFunnel.name) {
      setIsEditing(false);
      return;
    }

    setLoading(true);
    try {
      await editMultichatFunnelService(currentFunnel.id, {
        name: funnelName,
      });

      setCurrentMultichatFunnel({
        ...currentFunnel,
        name: funnelName,
      });

      showAlert('Funnel actualizado correctamente', {type: 'success'});
      setIsEditing(false);
    } catch (error) {
      showAlert('Error al actualizar el nombre del funnel', {type: 'error'});
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container className={classes.container} spacing={2}>
      <Grid item xs={12}>
        <div className={classes.header}>
          <div className={classes.nameContainer}>
            {isEditing ? (
              <TextField
                value={funnelName}
                onChange={e => setFunnelName(e.target.value)}
                disabled={loading}
                autoFocus
                size="small"
                variant="outlined"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSaveEdit();
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
              />
            ) : (
              <Typography className={classes.name}>
                {currentFunnel.name}
              </Typography>
            )}

            {!isEditing ? (
              <IconButton size="small" onClick={handleEditClick}>
                <Edit fontSize="small" />
              </IconButton>
            ) : (
              <div className={classes.actions}>
                <IconButton
                  size="small"
                  onClick={handleSaveEdit}
                  disabled={loading || !funnelName.trim()}
                >
                  <Check fontSize="small" style={{color: colors.success}} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCancelEdit}
                  disabled={loading}
                >
                  <Close fontSize="small" style={{color: colors.error}} />
                </IconButton>
              </div>
            )}
          </div>
        </div>
      </Grid>
      <Grid item xs={12}>
        <FunnelColumnList draggingType={draggingType} search={search} />
      </Grid>
    </Grid>
  );
};

export default FunnelItem;
